import React, { useMemo } from "react"
import { Container, Row, Col } from "reactstrap"
import ReactApexChart from "react-apexcharts"
import { DashStyles, DashHead, DateChip, fmtM, PALETTE, useChartFix } from "../Dashboard/dashkit"

/*
 * Finance Overview (static) — modelled on Anil's "Financial Performance" reference.
 * P&L KPIs, income-statement waterfall, actual-vs-target, monthly growth, receivable/
 * inventory days, headcount. Numbers are placeholders; wire per-scope later.
 */
const P = PALETTE

const KPIS = [
  { label: "Profit Ratio", value: "28.6%", target: "104%", yoy: "+4%", up: true, color: P.indigo, spark: [22, 24, 25, 26, 27, 28, 28.4, 28.6] },
  { label: "Fund Flow", value: fmtM(22_100_000), target: "98%", yoy: "+2%", up: true, color: P.green, spark: [16, 17, 18, 19, 20, 21, 21.6, 22.1] },
  { label: "Current Ratio", value: "1.82 : 1", target: "110%", yoy: "+7%", up: true, color: P.violet, spark: [1.4, 1.5, 1.55, 1.6, 1.68, 1.74, 1.79, 1.82] },
  { label: "Receivable/Payable", value: "1.34 : 1", target: "96%", yoy: "-3%", up: false, color: P.amber, spark: [1.5, 1.46, 1.44, 1.4, 1.38, 1.36, 1.35, 1.34] },
  { label: "Cash/Bank Accounts", value: fmtM(11_320_000), target: "102%", yoy: "+5%", up: true, color: P.cyan, spark: [8, 8.6, 9.2, 9.8, 10.3, 10.8, 11.1, 11.32] },
  { label: "Inventory Analysis", value: fmtM(8_400_000), target: "94%", yoy: "-1%", up: false, color: P.sky, spark: [9, 8.9, 8.8, 8.7, 8.6, 8.5, 8.45, 8.4] },
  { label: "Debt/Equity Ratio", value: "0.62 : 1", target: "112%", yoy: "+6%", up: true, color: P.teal, spark: [0.8, 0.76, 0.72, 0.7, 0.67, 0.65, 0.63, 0.62] },
  { label: "Net Profit Ratio", value: "12.9%", target: "108%", yoy: "+3%", up: true, color: P.red, spark: [9, 9.8, 10.4, 11, 11.6, 12.1, 12.6, 12.9] },
]

const MONTHS = ["Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar", "Apr", "May", "Jun"]

const FinanceOverview = ({ embedded = false }) => {
  if (!embedded) document.title = "Finance Overview | Calyvern"
  useChartFix()

  // income statement as a vertical bar chart (P&L line items, y-axis = amounts)
  const incomeBar = useMemo(() => ({
    options: {
      chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
      plotOptions: { bar: { columnWidth: "55%", borderRadius: 4, distributed: true } },
      colors: [P.indigo, P.red, P.cyan, P.red, P.cyan, P.red, P.green],
      dataLabels: { enabled: false },
      legend: { show: false },
      xaxis: {
        categories: ["Total Revenue", "Cost of Goods Sold", "Gross Profit", "Operating Expense", "Operating Income", "Other Expense / Tax", "Net Profit"],
        labels: { rotate: -35, style: { colors: "#94a3b8", fontSize: "10px" } },
        axisTicks: { show: false },
      },
      yaxis: { labels: { formatter: v => `${Math.round(v)}M`, style: { colors: "#94a3b8" } } },
      grid: { borderColor: "#eef0f4", strokeDashArray: 4 },
      tooltip: { y: { formatter: v => `${Math.round(v)}M` } },
    },
    series: [{ name: "Amount", data: [108, 60, 48, 30, 18, 4, 14] }],
  }), [])

  const salesVsTarget = useMemo(() => ({
    options: {
      chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
      plotOptions: { bar: { columnWidth: "60%", borderRadius: 3 } },
      colors: [P.cyan, "#cbd5e1"],
      dataLabels: { enabled: false },
      xaxis: { categories: MONTHS, labels: { style: { colors: "#94a3b8", fontSize: "10px" } } },
      yaxis: { labels: { formatter: v => `${v}M`, style: { colors: "#94a3b8" } } },
      grid: { borderColor: "#eef0f4", strokeDashArray: 4 },
      legend: { show: false },
    },
    series: [
      { name: "Actual", data: [7.6, 8.1, 8.4, 9.0, 8.7, 9.3, 8.9, 9.6, 10.1, 9.8, 10.4, 11.0] },
      { name: "Target", data: [8, 8, 8.5, 8.5, 9, 9, 9, 9.5, 9.5, 10, 10, 10.5] },
    ],
  }), [])

  // monthly sales as a vertical bar chart (x-axis = month, y-axis = amount)
  const salesBar = useMemo(() => ({
    options: {
      chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
      plotOptions: { bar: { columnWidth: "55%", borderRadius: 4 } },
      colors: [P.indigo],
      dataLabels: { enabled: false },
      legend: { show: false },
      xaxis: { categories: MONTHS, labels: { style: { colors: "#94a3b8", fontSize: "10px" } } },
      yaxis: { labels: { formatter: v => `${v}M`, style: { colors: "#94a3b8" } } },
      grid: { borderColor: "#eef0f4", strokeDashArray: 4 },
      tooltip: { y: { formatter: v => `${v}M` } },
    },
    series: [{ name: "Sales", data: [7.6, 8.1, 8.4, 9.0, 8.7, 9.3, 8.9, 9.6, 10.1, 9.8, 10.4, 11.0] }],
  }), [])

  const headcount = useMemo(() => ({
    options: {
      chart: { type: "donut", fontFamily: "inherit" },
      labels: ["Direct", "Indirect", "Contract"], colors: [P.indigo, P.green, P.amber],
      legend: { show: false }, dataLabels: { enabled: false }, stroke: { width: 0 },
      plotOptions: { pie: { donut: { size: "72%", labels: { show: true, total: { show: true, label: "Headcount", fontSize: "12px", color: "#94a3b8", formatter: () => "126" }, value: { fontSize: "24px", fontWeight: 700, color: "#0f172a" } } } } },
    },
    series: [72, 34, 20],
  }), [])

  const gauge = (val, color) => ({
    chart: { type: "radialBar", sparkline: { enabled: true }, fontFamily: "inherit" },
    plotOptions: { radialBar: { hollow: { size: "58%" }, track: { background: "#eef0f4" }, dataLabels: { name: { show: false }, value: { fontSize: "20px", fontWeight: 700, color: "#0f172a", offsetY: 6, formatter: v => `${Math.round(v)}` } } } },
    colors: [color], stroke: { lineCap: "round" },
  })

  const empCostVsSales = useMemo(() => ({
    options: {
      chart: { type: "line", toolbar: { show: false }, fontFamily: "inherit" },
      stroke: { width: [0, 3], curve: "smooth" }, colors: [P.indigo, P.amber],
      plotOptions: { bar: { columnWidth: "45%", borderRadius: 3 } },
      dataLabels: { enabled: false }, legend: { show: false },
      xaxis: { categories: MONTHS, labels: { style: { colors: "#94a3b8", fontSize: "10px" } } },
      yaxis: [{ labels: { formatter: v => `${v}M`, style: { colors: "#94a3b8" } } }, { opposite: true, labels: { formatter: v => `${v}%`, style: { colors: "#94a3b8" } } }],
      grid: { borderColor: "#eef0f4", strokeDashArray: 4 },
    },
    series: [
      { name: "Sales", type: "column", data: [7.6, 8.1, 8.4, 9, 8.7, 9.3, 8.9, 9.6, 10.1, 9.8, 10.4, 11] },
      { name: "Emp cost %", type: "line", data: [22, 21, 20, 19, 20, 18, 19, 18, 17, 18, 16, 15] },
    ],
  }), [])

  return (
    <React.Fragment>
      <DashStyles />
      <div className={embedded ? "dk-wrap" : "page-content dk-wrap"}>
        <Container fluid>
          <DashHead title="Finance Overview" welcome="Profit & loss, cash and working-capital at a glance." right={<DateChip />} />

          <Row className="g-3">
            {KPIS.map(k => (
              <Col key={k.label} md={6} xl={3}>
                <div className="dk-card dk-kpi">
                  <div className="dk-kpi-label">{k.label}</div>
                  <div className="dk-kpi-value">{k.value}</div>
                  <div className="dk-kpi-metrics">
                    <span className="m">Target<b>{k.target}</b></span>
                    <span className="m">vs Last Yr<b style={{ color: k.up ? "#16a34a" : "#dc2626" }}>{k.yoy}</b></span>
                  </div>
                  <div className="dk-kpi-spark"><Sparklet data={k.spark} color={k.color} /></div>
                </div>
              </Col>
            ))}
          </Row>

          <Row className="g-3 mt-1">
            <Col xxl={7}>
              <div className="dk-card h-100">
                <div className="dk-card-head"><span>INCOME STATEMENT</span><span className="dk-chip">FY 2024</span></div>
                <ReactApexChart options={incomeBar.options} series={incomeBar.series} type="bar" height={300} />
              </div>
            </Col>
            <Col xxl={5}>
              <div className="dk-card h-100">
                <div className="dk-card-head"><span>MONTHLY SALES</span></div>
                <ReactApexChart options={salesBar.options} series={salesBar.series} type="bar" height={300} />
              </div>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col xxl={7}>
              <div className="dk-card h-100">
                <div className="dk-card-head"><span>ACTUAL SALES vs TARGET</span></div>
                <div className="dk-legend"><span><i className="dk-dot" style={{ background: P.cyan }} />Actual</span><span><i className="dk-dot" style={{ background: "#cbd5e1" }} />Target</span></div>
                <ReactApexChart options={salesVsTarget.options} series={salesVsTarget.series} type="bar" height={280} />
              </div>
            </Col>
            <Col xxl={5}>
              <div className="dk-card h-100">
                <div className="dk-card-head"><span>HEADCOUNT</span></div>
                <ReactApexChart options={headcount.options} series={headcount.series} type="donut" height={230} />
                <ul className="dk-list">
                  <li><span className="dk-dot" style={{ background: P.indigo }} /><span className="nm">Direct labour</span><span className="vl">72</span></li>
                  <li><span className="dk-dot" style={{ background: P.green }} /><span className="nm">Indirect / staff</span><span className="vl">34</span></li>
                  <li><span className="dk-dot" style={{ background: P.amber }} /><span className="nm">Contract</span><span className="vl">20</span></li>
                </ul>
              </div>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col xxl={4}>
              <div className="dk-card h-100">
                <div className="dk-card-head"><span>EMPLOYEE COST vs SALES</span></div>
                <ReactApexChart options={empCostVsSales.options} series={empCostVsSales.series} type="line" height={250} />
              </div>
            </Col>
            <Col md={6} xxl={4}>
              <div className="dk-card h-100">
                <div className="dk-card-head"><span>DAYS RECEIVABLE OUTSTANDING</span></div>
                <ReactApexChart options={gauge(0.42, P.amber)} series={[42]} type="radialBar" height={190} />
                <div className="dk-gauge-center"><span>days · target ≤ 45</span></div>
                <div className="dk-legend mt-2"><span>Receivable aging</span></div>
                <div className="d-flex gap-2 mt-1">
                  {[["0–30", 62, P.green], ["31–60", 24, P.amber], ["61–90", 10, P.sky], ["90+", 4, P.red]].map(a => (
                    <div key={a[0]} style={{ flex: 1, textAlign: "center" }}>
                      <div className="dk-progress" style={{ height: 42, display: "flex", alignItems: "flex-end" }}>
                        <span style={{ width: "100%", height: `${a[1]}%`, background: a[2], borderRadius: 4, display: "block" }} />
                      </div>
                      <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 4 }}>{a[0]}<br />{a[1]}%</div>
                    </div>
                  ))}
                </div>
              </div>
            </Col>
            <Col md={6} xxl={4}>
              <div className="dk-card h-100">
                <div className="dk-card-head"><span>DAYS INVENTORY OUTSTANDING</span></div>
                <ReactApexChart options={gauge(0.36, P.violet)} series={[36]} type="radialBar" height={190} />
                <div className="dk-gauge-center"><span>days · target ≤ 40</span></div>
                <div className="dk-legend mt-2"><span>Inventory trend</span></div>
                <ReactApexChart
                  type="area" height={70}
                  series={[{ data: [44, 42, 45, 41, 43, 39, 40, 37, 38, 36] }]}
                  options={{ chart: { sparkline: { enabled: true } }, stroke: { curve: "smooth", width: 2.5 }, colors: [P.violet], fill: { type: "gradient", gradient: { opacityFrom: 0.3, opacityTo: 0.02 } }, tooltip: { enabled: false } }}
                />
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

// tiny inline sparkline (kept local to avoid extra import churn)
const Sparklet = ({ data, color }) => (
  <ReactApexChart
    type="area" height={56}
    series={[{ data }]}
    options={{ chart: { sparkline: { enabled: true } }, stroke: { curve: "smooth", width: 2.5 }, colors: [color], fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 100] } }, tooltip: { enabled: false } }}
  />
)

export default FinanceOverview
